import { Button, Divider, Typography } from "@mui/material";
import { Agendamento } from "../../../interfaces/entities/Agendamento";
import { ScheduleCardProps } from "../../../interfaces/props/SaudeOnline/ScheduleCardProps";
import ScheduleDetailFrame from "./ScheduleDetailFrame";
import { sxButtonCancelar, sxButtonScheduleAction, sxDividerScheduleCard } from "./sxProps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarLines } from "@fortawesome/pro-duotone-svg-icons";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTimeToISOFormat } from "../../../utils/DateFormat";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import DefaultButton from "../../../components/Accessibility/DefaultButton";

const ScheduleCard: React.FC<ScheduleCardProps> = memo((props) => {

    const agendamento: Agendamento = props.agendamento;
    const navigate = useNavigate();
    const accessibility = useAccessibility();

    const getProviderAdress = (): string => {
        return (!isNullOrEmpty(agendamento.logradouroPrestador) ? agendamento.logradouroPrestador + ", " : " ") +
            (!isNullOrEmpty(agendamento.numEnderPrestador) ? agendamento.numEnderPrestador + " " : " ") +
            (!isNullOrEmpty(agendamento.complEnderPrestador) ? agendamento.complEnderPrestador + " - " : " ") +
            (!isNullOrEmpty(agendamento.cidadePrestador) ? agendamento.cidadePrestador + " - " : " ") +
            (!isNullOrEmpty(agendamento.ufPrestador) ? agendamento.ufPrestador : " ");
    };

    const getScheduleDateTimeViewFormat = (): string => {
        return agendamento.dataAgendamento + " " + agendamento.horaAgendamento + ":00";
    }

    const getCancellationDate = (): string | null => {
        return agendamento.status === 'cancelado' && agendamento.dataCancelamento !== null ? "em " + agendamento.dataCancelamento : null;
    }

    const isNullOrEmpty = (value: string) => {
        return value === null || value === "" ? true : false;
    }

    const isConfirmableSchedule = (): boolean => {
        let nowDateTime = new Date();
        let scheduleDateTime = new Date(formatDateTimeToISOFormat(agendamento.dataAgendamento, agendamento.horaAgendamento));
        let confirmableDate = nowDateTime <= scheduleDateTime;
        let telemedicina = agendamento.tipoAtend === 'TELEMEDICINA' || agendamento.tipoAtend === 'TELEMEDICINA - RETORNO';
        let aguardandoConfirmacaoBenef = agendamento.codStatusAgenda === 'CONFBENEF' && agendamento.permiteConfirmacaoAgendamento === true;

        let confirmable = confirmableDate && telemedicina && aguardandoConfirmacaoBenef;
        return confirmable;
    }

    const isCancellableSchedule = (): boolean => {
        let nowDateTime = new Date();
        let scheduleDateTime = new Date(formatDateTimeToISOFormat(agendamento.dataAgendamento, agendamento.horaAgendamento));
        let cancellableDate = nowDateTime <= scheduleDateTime;
        let cancellableStatus = agendamento.status === 'confirmado' || agendamento.status === 'agendado';

        let cancellable = cancellableDate && cancellableStatus;
        return cancellable;
    }

    const isJoinableSchedule = (): boolean => {
        let telemedicina = agendamento.tipoAtend === 'TELEMEDICINA' || agendamento.tipoAtend === 'TELEMEDICINA - RETORNO';
        let joinableStatus = agendamento.status === 'confirmado' || agendamento.status === 'agendado';

        let joinable = telemedicina && joinableStatus;
        return joinable;
    }

    const disableJoin = (): boolean => {
        let enable = agendamento.statusTelemedicina === 'disponivel' && agendamento.statusTelemedicinaAPI === 'Iniciada';
        return !enable;
    }

    const handleClickIngressarNoAtendimento = () => {
        navigate(`/saudeOnline/ingressarNoAtendimento/${agendamento.idReuniao}`);
    }

    return (
        <div className='schedule-card' tabIndex={0} style={accessibility.contrastStyle}>
            <div>
                <FontAwesomeIcon  style={accessibility.contrastColorAccessibility} icon={faCalendarLines} className='fadt-calendar-lines' />
            </div>
            <div className='schedule-card-details'>
                <ScheduleDetailFrame
                    scheduleDetailKey={'medico'}
                    scheduleDetailValue={agendamento.nomeMedico}
                />
                <ScheduleDetailFrame
                    scheduleDetailKey={'especialidade'}
                    scheduleDetailValue={agendamento.especialidade} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'endereco'}
                    scheduleDetailValue={getProviderAdress()} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'telefone'}
                    scheduleDetailValue={!isNullOrEmpty(agendamento.fonePrestador) ? agendamento.fonePrestador : '-'} />
                <Divider sx={sxDividerScheduleCard} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'dataHoraAgendamento'}
                    scheduleDetailValue={getScheduleDateTimeViewFormat()} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'tipoAtend'}
                    scheduleDetailValue={agendamento.tipoAtend} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'dataConfirmacao'}
                    scheduleDetailValue={!isNullOrEmpty(agendamento.dataConfirmacao) ? agendamento.dataConfirmacao : '-'} />
                <ScheduleDetailFrame
                    scheduleDetailKey={'status'}
                    scheduleDetailValue={agendamento.status}
                    scheduleDetailAditionalValue={getCancellationDate()} />
                {(isCancellableSchedule() || isJoinableSchedule() || isConfirmableSchedule()) &&
                    <div className='schedule-action-buttons-holder'>
                        {isConfirmableSchedule() && <Button
                            type='button'
                            variant='contained'
                            sx={sxButtonScheduleAction}
                            onClick={props.handleClickConfirmarAgendamento}
                        >
                            <Typography style={accessibility.textStyleAccessibility}>Confirmar Agendamento</Typography>
                        </Button>}
                        {isJoinableSchedule() && <Button
                            type='button'
                            variant='contained'
                            disabled={disableJoin()}
                            onClick={handleClickIngressarNoAtendimento}
                            sx={sxButtonScheduleAction}
                        >
                            <Typography style={accessibility.textStyleAccessibility}>Ingressar no Atendimento</Typography>
                        </Button>}
                        {isCancellableSchedule() && <DefaultButton
                            type='button'
                            variant='outlined'
                            sx={sxButtonCancelar}
                            onClick={props.handleClickCancelarAtendimento}
                            style={accessibility.buttonStyleAccessibility}
                            aria-label={`Cancelar atendimento com ${agendamento.nomeMedico}, especialista em ${agendamento.especialidade} em ${agendamento.dataConfirmacao}`}
                            text="Cancelar Atendimento"
                        />}
                    </div>}
            </div>
        </div>
    )
});

export default ScheduleCard;
